import * as React from 'react';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { LocalNotifications } from '@capacitor/local-notifications';
import { Device } from '@capacitor/device';
import { Capacitor } from '@capacitor/core';
import { useReactiveVar } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { IosSilentNotifications } from '@aparajita/capacitor-ios-silent-notifications';

import {
  useSaveUserPushTokenMutation,
  Notification,
  NotificationTypeEnum,
  NotificationActionEnum,
} from '@graphql';
import {
  loginStatusVar,
  previewBookingVar,
  conversationIdVar,
  conversationDialogOpenVar,
  conversationOpenListVar,
} from '@context';

const isDEV = import.meta.env.DEV;

export const usePushNotifications = ({ initialize = false } = {}) => {
  const appIsActive = React.useRef(true);
  const isInitialized = React.useRef(false);
  const { isLoggedIn } = useReactiveVar(loginStatusVar);
  const navigate = useNavigate();

  const [saveUserPushToken] = useSaveUserPushTokenMutation();

  const removeAllListeners = async () => {
    await FirebaseMessaging.removeAllListeners();
  };

  /**
   * Ensure we have notification permission.
   * @returns true if permission is granted, false otherwise.
   */
  const ensurePermissions = async (): Promise<boolean> => {
    const status = await checkPermissions();

    if (status === 'granted') {
      return true; // everything is OK
    }

    if (status === 'denied') {
      import.meta.env.DEV && console.warn('Push permission previously denied.');
      // Optionally show a banner directing the user to system settings
      return false;
    }

    // status === 'prompt' → ask the user once
    const newStatus = await requestPermissions();
    if (newStatus === 'granted') {
      return true;
    }

    import.meta.env.DEV && console.warn('Push permission not granted after prompt.');
    return false;
  };

  const checkPermissions = async () => {
    const result = await FirebaseMessaging.checkPermissions();
    return result.receive;
  };

  const requestPermissions = async () => {
    const result = await FirebaseMessaging.requestPermissions();
    return result.receive;
  };

  const getToken = async () => {
    const result = await FirebaseMessaging.getToken();
    return result.token;
  };

  const deleteToken = async () => {
    await FirebaseMessaging.deleteToken();
  };

  const getDeliveredNotifications = async () => {
    const result = await FirebaseMessaging.getDeliveredNotifications();
    return result.notifications;
  };

  const removeDeliveredNotifications = async (notifications = []) => {
    if (notifications.length) {
      await FirebaseMessaging.removeDeliveredNotifications({ notifications });
    }
  };

  const removeAllDeliveredNotifications = async () => {
    await FirebaseMessaging.removeAllDeliveredNotifications();
  };

  const subscribeToTopic = async () => {
    await FirebaseMessaging.subscribeToTopic({ topic: 'news' });
  };

  const unsubscribeFromTopic = async () => {
    await FirebaseMessaging.unsubscribeFromTopic({ topic: 'news' });
  };

  const addTokenReceivedListener = async () => {
    await FirebaseMessaging.addListener('tokenReceived', (event) => {
      isDEV && console.log('🔐 tokenReceived', { event });
    });
  };

  const addNotificationReceivedListener = async () => {
    await FirebaseMessaging.addListener('notificationReceived', async (event) => {
      try {
        isDEV && console.log('📩 [notificationReceived]', event);
        const currentConversationId = conversationIdVar();
        const isConversationListOpen = conversationOpenListVar();

        const { title, body } = event.notification;
        const { type, entityId } = event.notification.data as Partial<
          Notification & { title: string; body: string }
        >;

        let shouldHide = false;

        // handle every case where we don't want to show a local notification
        if (
          type === NotificationTypeEnum.Message &&
          ((isConversationListOpen && !currentConversationId) || entityId === currentConversationId)
        ) {
          shouldHide = true;
        }

        if (shouldHide) {
          return;
        }

        const newNotification = {
          id: Date.now(),
          title: title ?? '',
          body: body ?? '',
          extra: event.notification.data,
        };

        isDEV && console.info('⏱️ Local notification scheduled', newNotification);

        // Sinon, app active + pas sur la bonne conv → on affiche UNE notif locale
        try {
          if (title?.length || body?.length) {
            await LocalNotifications.schedule({
              notifications: [newNotification],
            });
          }
        } catch (err) {
          isDEV && console.warn('[SILENT LOCAL NOTIF ERROR]', err);
        }
      } catch (error) {
        isDEV && console.error('[NOTIFICATION RECEIVED ERROR]', error);
      }
    });
  };

  const addNotificationActionPerformedListener = async () => {
    const handleTap = (data: Partial<Notification & { to?: string; authTo?: string }>) => {
      const {
        type,
        action,
        id, // this is notification id
        entityId, // this is the related entity id (e.g., booking id)
        to,
      } = data;

      if (type === NotificationTypeEnum.Message && entityId) {
        conversationIdVar(entityId);
        conversationDialogOpenVar(true);
      } else if (type === NotificationTypeEnum.Booking && entityId) {
        // trigger dialog without navigating (would work but not necessary)
        previewBookingVar(entityId);
      } else if (data.authTo || data.to) {
        const to = isLoggedIn && data.authTo ? data.authTo : (data.to as string);
        navigate(to, { preventScrollReset: true });
      }
    };

    // Firebase notification tap handler
    await FirebaseMessaging.addListener('notificationActionPerformed', (event) => {
      const { actionId, notification } = event;
      isDEV && console.log('📲 notificationActionPerformed', { actionId: event.actionId, notification });
      if (actionId === 'tap') {
        handleTap(notification.data as Partial<Notification & { to?: string; authTo?: string }>);
      }
    });

    // local notification tap handler
    await LocalNotifications.addListener('localNotificationActionPerformed', (event) => {
      const { notification, actionId } = event;
      isDEV && console.log('📲 [localNotificationActionPerformed]', { actionId, notification });
      if (actionId === 'tap') {
        handleTap(notification.extra as Partial<Notification & { to?: string; authTo?: string }>);
      }
    });
  };

  const addIosSilentNotifications = async () => {
    try {
      if (Capacitor.getPlatform() !== 'ios') {
        return;
      }

      await IosSilentNotifications.addListener('onSilentNotification', async (event) => {
        const currentConversationId = conversationIdVar();
        const isConversationListOpen = conversationOpenListVar();
        const data = event?.userInfo?.data ?? event?.data ?? ({} as any);

        isDEV &&
          console.log('[SILENT EVENT RAW]', {
            appIsActive: appIsActive.current,
            currentConversationId,
            isConversationListOpen,
            data,
            type: data?.type,
            MNT: NotificationTypeEnum.Message,
          });

        const { type, entityId, title, body } = data as Partial<
          Notification & { title: string; body: string }
        >;

        // handle every case where we don't want to show a local notification
        if (
          type !== NotificationTypeEnum.Message ||
          (isConversationListOpen && !currentConversationId) ||
          entityId === currentConversationId
        ) {
          return;
        }

        // Sinon, app active + pas sur la bonne conv → on affiche UNE notif locale
        try {
          if (title?.length && body?.length) {
            await LocalNotifications.schedule({
              notifications: [
                {
                  id: Date.now(),
                  title: title,
                  body: body,
                  extra: data,
                },
              ],
            });
          }
        } catch (err) {
          isDEV && console.warn('[SILENT LOCAL NOTIF ERROR]', err);
        }
      });
    } catch (err) {
      isDEV && console.warn('[SILENT SETUP ERROR]', err);
    }
  };

  const addListeners = async () => {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    isDEV && console.log('🔔 Adding mobile push notifications listeners...');
    await addTokenReceivedListener();
    await addNotificationReceivedListener();
    await addIosSilentNotifications();
    await addNotificationActionPerformedListener();
    isDEV && console.log('✅ [Manual] Mobile push notifications listeners added');
  };

  const register = async () => {
    // Push notifications only for native apps
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    isDEV && console.log('🔔 Registering mobile push notifications...');
    // 1. Check/request permission
    const ok = await ensurePermissions();

    if (!ok) {
      return; // abort if user denied
    }

    // 2. Mark as initialized
    isInitialized.current = true;

    // 3. Get FCM token and save it on the server
    const { token } = await FirebaseMessaging.getToken();

    const deviceInfo = await Device.getInfo();
    await saveUserPushToken({
      variables: {
        input: {
          token,
          platform: deviceInfo.platform,
          device: deviceInfo,
        },
      },
    });

    // 4. Add native listeners
    await addListeners();
  };

  const unregister = async () => {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    isDEV && console.log('🔔 Unegistering mobile push notifications...');
    isInitialized.current = false;
    await removeAllListeners();
  };

  const clearConversationNotifications = async (conversationId: string | null) => {
    try {
      if (!Capacitor.isNativePlatform() || !conversationId) {
        return;
      }

      const { notifications } = await FirebaseMessaging.getDeliveredNotifications();

      const related = notifications
        .filter((n) => {
          const data = n.data as any;
          const type = data.type ?? (data.cap_extra?.type as Notification['type']);
          const entityId = data.entityId ?? (data.cap_extra?.entityId as Notification['entityId']);
          return type === NotificationTypeEnum.Message && entityId === conversationId;
        })
        .map((n) => ({ id: n.id }));

      if (!related.length) {
        return;
      }

      await FirebaseMessaging.removeDeliveredNotifications({
        notifications: related,
      });

      isDEV && console.log(`[NOTIFS] cleared ${related.length} for conversation`, conversationId);
    } catch (err) {
      isDEV && console.warn('[NOTIFS] clearConversationNotifications error', err);
    }
  };

  React.useEffect(() => {
    if (initialize) {
      if (isLoggedIn && !isInitialized.current) {
        register();
      } else if (!isLoggedIn && isInitialized.current) {
        unregister();
      }
    }
  }, [isLoggedIn, initialize]);

  return {
    addListeners,
    register,
    unregister,
    checkPermissions,
    requestPermissions,
    getToken,
    deleteToken,
    getDeliveredNotifications,
    removeDeliveredNotifications,
    removeAllDeliveredNotifications,
    subscribeToTopic,
    unsubscribeFromTopic,
    addTokenReceivedListener,
    addNotificationReceivedListener,
    addNotificationActionPerformedListener,
    removeAllListeners,
    clearConversationNotifications,
  };
};
